import React, { useState } from "react"

import { Popover, PopoverAnchor, PopoverContent } from "@coval/ui"

import { Message as TranscriptMessage } from "@/components/transcript/Message"
import { messageSpeaker } from "@/components/transcript/messageSpeaker"
import { getMessageStartLabel } from "@/components/transcript/TimestampDisplay"
import type { TranscriptParticipantLabels } from "@/components/transcript/transcriptParticipants"
import type { Message } from "@/types/Transcript"

interface MessageReferenceChipProps {
  /** The referenced message, absent when the transcript has not loaded. */
  message?: Message
  /** Position in the transcript, 0-based, as the metric output records it. */
  messageIndex: number
  participants?: TranscriptParticipantLabels
  onSelect: (messageIndex: number) => void
  onPreviewOpenChange: (isOpen: boolean) => void
}

/**
 * One referenced message, named by the time it was spoken so it matches the
 * timestamp under the transcript bubble it points at. A transcript with no
 * timing — a text conversation — has no such label, so the chip falls back to
 * the message's position.
 *
 * Pointing at the chip shows the turn itself, drawn as the transcript draws it
 * rather than as a quotation: the reviewer recognises the bubble they are being
 * sent to before they travel to it.
 */
export const MessageReferenceChip: React.FC<MessageReferenceChipProps> = ({
  message,
  messageIndex,
  participants,
  onSelect,
  onPreviewOpenChange,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const timeLabel = message ? getMessageStartLabel(message) : null
  const label = timeLabel ?? `msg ${messageIndex + 1}`
  const isPersona = message ? messageSpeaker(message) === "persona" : false
  const speakerLabel = isPersona ? "User" : "Agent"

  const setPreviewOpen = (isOpen: boolean) => {
    setIsPreviewOpen(isOpen)
    onPreviewOpenChange(isOpen)
  }

  const trigger = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onSelect(messageIndex)
      }}
      onMouseEnter={() => setPreviewOpen(true)}
      onMouseLeave={() => setPreviewOpen(false)}
      onFocus={() => setPreviewOpen(true)}
      onBlur={() => setPreviewOpen(false)}
      aria-label={`Jump to ${speakerLabel} message at ${label}`}
      className="rounded font-medium text-coval-info-border hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-coval-info-border"
    >
      {label}
    </button>
  )

  // Without the message there is nothing to draw, and a preview holding only
  // the label the chip already shows is noise.
  if (!message) {
    return trigger
  }

  return (
    <Popover open={isPreviewOpen}>
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverContent
        side="top"
        align="start"
        collisionPadding={8}
        // The preview follows the pointer's chip, so it must never take focus
        // or swallow the pointer that is still travelling over the chips. It
        // opens on focus too, and a focus scope that moved focus on the way in
        // or out would fight the keyboard walking along the row.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // No surface of its own: the bubble is the overlay, so a panel behind
        // it would read as a second card around a card. The width is the one a
        // bubble has in the transcript rail — four fifths of a w-96 rail's
        // content box — so this reads as the same object, and the bubble fills
        // it so that its left edge lands on the chip that opened it.
        className="pointer-events-none w-[272px] border-0 bg-transparent p-0 shadow-none"
      >
        <TranscriptMessage
          message={message}
          index={messageIndex}
          fillWidth
          agentLabel={participants?.agentLabel}
          userLabel={participants?.userLabel}
          agentAvatarUrl={participants?.agentAvatarUrl}
          userAvatarUrl={participants?.userAvatarUrl}
          agentAvatarTint={participants?.agentAvatarTint}
          userAvatarTint={participants?.userAvatarTint}
        />
      </PopoverContent>
    </Popover>
  )
}
